import { useState, useEffect, useRef } from 'react';
import ButtonCode from '../ButtonCode';
import CopyButton from '../common/CopyButton';
import { SketchPicker } from 'react-color';
import fetchWithToken from '../../services';

const ButtonCustomizationTab = ({ buttonId }) => {
    console.log('Button ID on Customize page: ', buttonId);
    const apiBaseUrl = process.env.REACT_APP_API_BASE_URL;
    const [customizationsLoaded, setCustomizationsLoaded] = useState(false);
    const scriptContainerRef = useRef(null);
    const buttonRef = useRef(null);
    const scriptRef = useRef(null);

    // Button customization state
    const [buttonText, setButtonText] = useState('Donate');
    const [bgColor, setBgColor] = useState('#68C7CC');
    const [textColor, setTextColor] = useState('#FFFFFF');
    const [buttonShape, setButtonShape] = useState('rounded');
    const [showBgColorPicker, setShowBgColorPicker] = useState(false);
    const [showTextColorPicker, setShowTextColorPicker] = useState(false);
    const [customizing, setCustomizing] = useState(false);
    const [scriptLoaded, setScriptLoaded] = useState(false);

    // Function to clean up the TiplButton global state
    const cleanupTiplButtonState = () => {
      console.log('Cleaning up TiplButton state');
      if (window.TiplButton) {
        window.TiplButton.initialized = false;

        // Remove any overlay elements that might have been created
        const overlay = document.getElementById('overlay');
        if (overlay) {
          overlay.remove();
        }
      }
    };

    // Load existing customizations when component mounts or buttonId changes
    useEffect(() => {
      if (buttonId && !customizationsLoaded) {
        fetchButtonCustomizations();
      }
    }, [buttonId, customizationsLoaded]);

    // Effect to handle script loading and cleanup
    useEffect(() => {
      // Only run if we have a buttonId and a container reference
      if (scriptContainerRef.current && buttonId) {
        console.log('Loading button script for preview...');

        // Clean up any existing content in the container
        while (scriptContainerRef.current.firstChild) {
          scriptContainerRef.current.removeChild(scriptContainerRef.current.firstChild);
        }

        // Reset the TiplButton state before adding a new script
        cleanupTiplButtonState();

        // Create and inject the script element
        const scriptElement = document.createElement('script');
        scriptElement.src = `${apiBaseUrl}/button-loader.js`;
        scriptElement.id = buttonId;
        scriptElement.async = true;

        // Save reference to script element for cleanup
        scriptRef.current = scriptElement;

        // Add a listener to know when the script is loaded
        scriptElement.onload = () => {
          console.log('Button script loaded successfully');
          setScriptLoaded(true);
        };

        // Add the script to the container
        scriptContainerRef.current.appendChild(scriptElement);
      }

      // Cleanup function when component unmounts or dependencies change
      return () => {
        console.log('Running cleanup for button script');

        // Remove the script element if it exists
        if (scriptRef.current) {
          try {
            scriptRef.current.remove();
          } catch (e) {
            console.error('Error removing script:', e);
          }
          scriptRef.current = null;
        }

        // Clean up the container
        if (scriptContainerRef.current) {
          while (scriptContainerRef.current.firstChild) {
            scriptContainerRef.current.removeChild(scriptContainerRef.current.firstChild);
          }
        }

        // Reset global state
        cleanupTiplButtonState();
        setScriptLoaded(false);
      };
    }, [buttonId, apiBaseUrl]); // Only re-run if buttonId or apiBaseUrl changes

    // Fetch button customizations from the server
    const fetchButtonCustomizations = async () => {
      try {
        const response = await fetch(`${apiBaseUrl}/button-customizations/${buttonId}`);
        if (response.ok) {
          const data = await response.json();
          if (data.customizations) {
            setBgColor(data.customizations.bgColor || '#68C7CC');
            setTextColor(data.customizations.textColor || '#FFFFFF');
            setButtonShape(data.customizations.buttonShape || 'rounded');
            setButtonText(data.customizations.buttonText || 'Donate');
            setCustomizationsLoaded(true);
          }
        }
      } catch (error) {
        console.error('Error fetching button customizations:', error);
      }
    };

    // Save button customizations to the server
    const saveButtonCustomizations = async () => {
      setCustomizing(true);
      try {
        const customizations = {
          bgColor,
          textColor,
          buttonShape,
          buttonText
        };

        const response = await fetchWithToken(`/button-customizations/${buttonId}`, {
          method: 'POST',
          body: JSON.stringify({ customizations }),
        });

        if (response.ok) {
          // Update button styling directly without reloading script
          updateButtonStyling();
        } else {
          console.error('Failed to save button customizations');
        }
      } catch (error) {
        console.error('Error saving button customizations:', error);
      } finally {
        setCustomizing(false);
      }
    };

    // Helper function to update button styling directly
    const updateButtonStyling = () => {
      if (scriptContainerRef.current) {
        // Find the button element in the container
        const button = scriptContainerRef.current.querySelector('.donate-button');
        if (button) {
          // Apply current styling directly to the button
          button.textContent = buttonText;
          button.style.backgroundColor = bgColor;
          button.style.color = textColor;

          // Apply shape
          switch (buttonShape) {
            case 'sharp':
              button.style.borderRadius = '0';
              break;
            case 'rounded':
              button.style.borderRadius = '4px';
              break;
            case 'pill':
              button.style.borderRadius = '50px';
              break;
            default:
              button.style.borderRadius = '4px';
          }
        }
      }
    };

    // Predefined color options
    const colorPresets = [
      { name: 'Teal', value: '#68C7CC' },
      { name: 'Blue', value: '#007BFF' },
      { name: 'Green', value: '#28A745' },
      { name: 'Red', value: '#DC3545' },
      { name: 'Orange', value: '#FD7E14' },
      { name: 'Purple', value: '#6F42C1' },
      { name: 'Pink', value: '#E83E8C' },
      { name: 'Yellow', value: '#FFC107' },
      { name: 'Indigo', value: '#6610F2' },
      { name: 'Slate', value: '#6C757D' },
    ];

    // Handle changes to any customization option
    useEffect(() => {
      if (buttonId && !customizing && scriptLoaded) {
        // Save changes whenever any of the customization options change
        const saveTimeout = setTimeout(() => {
          saveButtonCustomizations();
        }, 500); // Debounce to avoid too many requests

        return () => clearTimeout(saveTimeout);
      }
    }, [bgColor, textColor, buttonShape, buttonText]);

    return (
      <div className="md:p-6">
        {buttonId ? (
          <div className="flex flex-col md:flex-row gap-6">
            {/* Customization options - top on mobile, left column on desktop */}
            <div className="w-full md:w-1/3">
              <div className="bg-white p-2 md:p-6 rounded-md space-y-6">

                {/* Button Text */}
                <div className="max-md:pb-5">
                  <label htmlFor="button-text-input" className="font-medium mb-2 block">Text:</label>
                  <input
                    id="button-text-input"
                    type="text"
                    value={buttonText}
                    onChange={(e) => setButtonText(e.target.value)}
                    className="w-60 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#68C7CC]"
                    placeholder="Button Text"
                    maxLength={30}
                  />
                  <div className="mt-2 flex flex-wrap gap-2">
                    {['Support me', 'Tip me', 'Donate'].map(text => (
                      <button
                        key={text}
                        onClick={() => setButtonText(text)}
                        className="px-3 py-1 text-sm bg-gray-100 hover:bg-gray-200 rounded-md transition-colors"
                      >
                        {text}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Text Color */}
                <div className="relative max-md:pb-5">
                  <h4 id="text-color-label" className="font-medium mb-2">Text color:</h4>
                  <div className="flex flex-wrap gap-2 mb-2">
                    {['#FFFFFF', '#000000', '#333333', '#666666'].map(color => (
                      <button
                        key={color}
                        onClick={() => setTextColor(color)}
                        className={`w-8 h-8 rounded-full ${color === '#FFFFFF' ? 'border border-gray-300' : ''} flex items-center justify-center`}
                        style={{ backgroundColor: color }}
                      >
                        {textColor === color && (
                          <span style={{ color: color === '#FFFFFF' ? '#000000' : '#FFFFFF' }}>✓</span>
                        )}
                      </button>
                    ))}
                  </div>
                  <div className="flex gap-2 items-center">
                    <div
                      role="button"
                      aria-label="Open text color picker"
                      tabIndex="0"
                      className="w-10 h-10 rounded-md border border-gray-300 cursor-pointer"
                      style={{ backgroundColor: textColor }}
                      onClick={() => setShowTextColorPicker(!showTextColorPicker)}
                      onKeyDown={(e) => e.key === 'Enter' && setShowTextColorPicker(!showTextColorPicker)}
                    ></div>
                    <input
                      type="text"
                      aria-label="Text color hex value"
                      value={textColor}
                      onChange={(e) => setTextColor(e.target.value)}
                      className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#68C7CC] w-32"
                    />
                  </div>
                  {showTextColorPicker && (
                    <div className="absolute z-10 bottom-full mb-2">
                      <div className="fixed inset-0" onClick={() => setShowTextColorPicker(false)}></div>
                      <SketchPicker
                        color={textColor}
                        onChange={(color) => setTextColor(color.hex)}
                      />
                    </div>
                  )}
                </div>

                {/* Background Color */}
                <div className="relative max-md:pb-5">
                  <h4 id="bg-color-label" className="font-medium mb-2">Background color:</h4>
                  <div role="radiogroup" aria-labelledby="bg-color-label" className="flex flex-wrap gap-2 mb-2 md:max-w-48">
                  {colorPresets.map(color => (
                      <button
                        key={color.name}
                        role="radio"
                        aria-checked={bgColor === color.value}
                        aria-label={`Set background color to ${color.name}`}
                        onClick={() => setBgColor(color.value)}
                        className="w-8 h-8 rounded-full flex items-center justify-center"
                        style={{ backgroundColor: color.value }}
                        title={color.name}
                      >
                        {bgColor === color.value && (
                          <span className="text-white text-xl" aria-hidden="true">✓</span>
                        )}
                      </button>
                    ))}
                  </div>
                  <div className="flex gap-2 items-center">
                    <div
                      role="button"
                      aria-label="Open background color picker"
                      tabIndex="0"
                      className="w-10 h-10 rounded-md border border-gray-300 cursor-pointer"
                      style={{ backgroundColor: bgColor }}
                      onClick={() => setShowBgColorPicker(!showBgColorPicker)}
                      onKeyDown={(e) => e.key === 'Enter' && setShowBgColorPicker(!showBgColorPicker)}
                    ></div>
                    <input
                      type="text"
                      aria-label="Background color hex value"
                      value={bgColor}
                      onChange={(e) => setBgColor(e.target.value)}
                      className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#68C7CC] w-32"
                    />
                  </div>
                  {showBgColorPicker && (
                    <div className="absolute z-10 bottom-full mb-2">
                      <div className="fixed inset-0" onClick={() => setShowBgColorPicker(false)}></div>
                      <SketchPicker
                        color={bgColor}
                        onChange={(color) => setBgColor(color.hex)}
                      />
                    </div>
                  )}
                </div>

                {/* Button Shape */}
                <div className="max-md:pb-5">
                  <h4 id="shape-label" className="font-medium mb-2">Shape:</h4>
                  <div role="radiogroup" aria-labelledby="shape-label" className="flex flex-wrap gap-3">
                    <button
                      role="radio"
                      aria-checked={buttonShape === 'sharp'}
                      onClick={() => setButtonShape('sharp')}
                      className={`p-2 border ${buttonShape === 'sharp' ? 'border-[#68C7CC] bg-[#68C7CC]/10' : 'border-gray-300'}`}
                    >
                      Square
                    </button>
                    <button
                      role="radio"
                      aria-checked={buttonShape === 'rounded'}
                      onClick={() => setButtonShape('rounded')}
                      className={`p-2 border rounded-md ${buttonShape === 'rounded' ? 'border-[#68C7CC] bg-[#68C7CC]/10' : 'border-gray-300'}`}
                    >
                      Rounded
                    </button>
                    <button
                      role="radio"
                      aria-checked={buttonShape === 'pill'}
                      onClick={() => setButtonShape('pill')}
                      className={`p-2 border rounded-full ${buttonShape === 'pill' ? 'border-[#68C7CC] bg-[#68C7CC]/10' : 'border-gray-300'}`}
                    >
                      Pill
                    </button>
                  </div>
                </div>

              </div>
            </div>

            {/* Preview pane - bottom on mobile, right side on desktop */}
            <div className="w-full md:w-2/3 space-y-6 p-2">
              {/* Button Preview */}
              <div className="bg-white md:p-6 rounded-md">
                <p className="mb-1 font-medium">Preview:</p>
                <div ref={scriptContainerRef} className="p-2 flex rounded mb-8 max-md:justify-center md:ml-[110px]"></div>

                {/* Button Code */}
                <div className="pt-6 md:w-80">
                  <h4 className="font-medium mb-3">Button code:</h4>
                  <div className="relative mr-6 inline-block">
                    <ButtonCode id={buttonId} />
                    <CopyButton
                      className="absolute top-2 -right-7"
                      content={`<script\n    src="${apiBaseUrl}/button-loader.js"\n    id="${buttonId}"\nasync></script>`}
                    />
                  </div>
                  <p className="pt-2 text-sm text-gray-600">Add this code to your website where you want the button to appear.</p>
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div className="bg-yellow-50 p-4 rounded-md border border-yellow-200 text-yellow-800">
            <p>No button code has been generated yet. Please complete your profile setup to get your custom tipping button.</p>
          </div>
        )}
      </div>
    );
  };

  export default ButtonCustomizationTab;